import React, { useEffect, useMemo, useState } from "react";
import Select, { ActionMeta, MultiValue } from "react-select";
import { useTranslation } from "react-i18next";
import { FaTimes } from "react-icons/fa";
import "react-datepicker/dist/react-datepicker.css";
import { selectStyles } from "../../ManageUserTab/types/SelectStyles";
import { ITeam } from "../../../interfaces/team.interface";
import { useQuery } from "@tanstack/react-query";
import { fetchTeamData } from "../../ManageUserTab/services/fetchTeamData.service";
import { fetchDefaultFilterData } from "../../../services/defaultFilters.service";
import moment from "moment-timezone";
import CreatableSelect from "react-select/creatable";

interface IAddNotificationModalProps {
  setShowAddNotificationModal: any;
  onSubmit: (notificationData: any) => void;
  darkMode: boolean;
  teamListOptions: { value: string; label: string }[];
  selectedTeam: ITeam | null;
  editNotification?: any | null;
}

const AddEditNotificationModal: React.FC<IAddNotificationModalProps> = ({
  setShowAddNotificationModal,
  onSubmit,
  darkMode,
  teamListOptions,
  selectedTeam,
  editNotification,
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(selectedTeam ? 2 : 1);
  const [subject, setSubject] = useState("");
  const [team, setTeam] = useState<{ value: string; label: string } | null>(
    selectedTeam
      ? { value: String(selectedTeam.id), label: selectedTeam.name }
      : null,
  );
  const selectedTeamOption =
    teamListOptions.find((o) => o.value === team?.value) ?? null;

  const [regulators, setRegulators] = useState<any[]>([]);
  const [classesInput, setClasses] = useState<any[]>([]);
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [selectedTime, setSelectedTime] = useState<Date | null>(new Date());
  const timezoneOptions = moment.tz.names().map((tz) => {
    const offset = moment.tz(tz).utcOffset();
    const sign = offset >= 0 ? "+" : "-";
    const hours = Math.floor(Math.abs(offset) / 60)
      .toString()
      .padStart(2, "0");
    const minutes = (Math.abs(offset) % 60).toString().padStart(2, "0");
    const formattedOffset = `UTC${sign}${hours}:${minutes}`;

    return {
      value: tz,
      label: `${tz} (${formattedOffset})`,
    };
  });
  const allClassesOption = { value: "ALL", label: "All classes" };
  const allRecipientsOption = { value: "ALL", label: "All recipients" };

  const [timezone, setTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  );
  const [members, setMembers] = useState<any[]>([]);
  const teamId = team?.value || selectedTeam?.id;
  const weekdays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  const generateTimeOptions = () => {
    const times = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 15) {
        const hh = h.toString().padStart(2, "0");
        const mm = m.toString().padStart(2, "0");
        const label = `${hh}:${mm}`;
        times.push({ value: label, label });
      }
    }
    return times;
  };

  const [timeOptions, setTimeOptions] = useState(generateTimeOptions());
  const [daysError, setDaysError] = useState(false);
  const [timeInput, setTimeInput] = useState(
    selectedTime ? moment(selectedTime).format("HH:mm") : "",
  );

  const { data: teamData, refetch: refetchTeamData } = useQuery({
    queryKey: ["teamData", teamId],
    queryFn: () => fetchTeamData(Number(teamId)),
    enabled: !!teamId,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const { data: defaultFilterData } = useQuery({
    queryKey: ["defaultFilterData", regulators],
    queryFn: () =>
      fetchDefaultFilterData({
        regulator: regulators.map((r: any) => r.label),
      }),
    enabled: regulators?.length !== 0,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    if (team?.value) {
      refetchTeamData();
    }
  }, [team?.value, refetchTeamData]);

  const handleDayToggle = (day: string) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day],
    );
  };

  const isStep2Valid = useMemo(() => {
    return (
      !!team?.value &&
      subject.trim().length > 0 &&
      regulators.length > 0 &&
      members.length > 0 &&
      selectedDays.length > 0 &&
      !!selectedTime &&
      !!timezone
    );
  }, [
    team,
    subject,
    regulators.length,
    members.length,
    selectedDays.length,
    selectedTime,
    timezone,
  ]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedDays.length === 0) {
      setDaysError(true);
      return;
    }
    setDaysError(false);

    if (
      !subject.trim() ||
      !team ||
      regulators.length === 0 ||
      members.length === 0 ||
      !selectedTime ||
      !timezone
    ) {
      return;
    }

    const hours = selectedTime.getHours().toString();
    const minutes = selectedTime.getMinutes().toString();

    const dayMapping: Record<string, number> = {
      Mon: 1,
      Tue: 2,
      Wed: 3,
      Thu: 4,
      Fri: 5,
      Sat: 6,
      Sun: 0,
    };

    const dayNumbers = selectedDays.map((day) => dayMapping[day]).join(",");

    const submittedClasses = classesInput.some((c) => c.value === "ALL")
      ? []
      : classesInput.map((c: any) => c.value);

    const submittedMembers = members.some((m) => m.value === "ALL")
      ? []
      : members.map((m) => m.value);

    const result = {
      id: editNotification?.id,
      team: Number(team.value),
      regulators: regulators.map((r) => r.value),
      classes: submittedClasses,
      subject: subject.trim(),
      members: submittedMembers,
      schedule: {
        minute: minutes,
        hour: hours,
        day_of_week: dayNumbers,
        timezone,
      },
    };

    onSubmit(result);
    setShowAddNotificationModal(false);
  };

  useEffect(() => {
    document.body.classList.add("overflow-hidden");
    return () => document.body.classList.remove("overflow-hidden");
  }, []);

  const getSelectStyles = (isDisabled: boolean = false) => ({
    ...selectStyles(darkMode, isDisabled),
    input: (base: any) => ({
      ...base,
      color: darkMode ? "#FFFFFF" : "#000000",
    }),
    option: (base: any, { isFocused }: any) => ({
      ...base,
      backgroundColor: isFocused ? "#86BC25" : darkMode ? "#374151" : "#FFFFFF",
      color: darkMode ? "#FFFFFF" : "#000000",
    }),
    multiValue: (base: any) => ({
      ...base,
      backgroundColor: darkMode ? "#43B02A" : "#26890D",
    }),
    multiValueLabel: (base: any) => ({ ...base, color: "#e2e8f0" }),
  });

  const handleNextFromTeam = (e: React.FormEvent) => {
    e.preventDefault();
    if (team) setStep(2);
  };

  const handleBackFromDetails = () => setStep(1);

  const selectedTimezoneOption = timezoneOptions.find(
    (tz) => tz.value === timezone,
  );

  useEffect(() => {
    if (editNotification) {
      setStep(2);
      setSubject(editNotification.subject);
      setTeam({
        value: String(editNotification.team),
        label:
          teamListOptions.find((t) => t.value === String(editNotification.team))
            ?.label || "",
      });

      setRegulators(
        editNotification.regulators.map((r: any) => ({
          value: r.id,
          label: r.name,
        })),
      );

      if (Array.isArray(editNotification.classes)) {
        if (editNotification.classes.length === 0) {
          setClasses([allClassesOption]);
        } else {
          setClasses(
            editNotification.classes.map((c: string) => ({
              value: c,
              label: c,
            })),
          );
        }
      }

      const reverseDayMapping: Record<number, string> = {
        1: "Mon",
        2: "Tue",
        3: "Wed",
        4: "Thu",
        5: "Fri",
        6: "Sat",
        0: "Sun",
      };
      const days = editNotification.schedule.day_of_week
        ?.split(",")
        .map((n: string) => reverseDayMapping[Number(n)]);
      setSelectedDays(days || []);

      const newDate = new Date();
      newDate.setHours(Number(editNotification.schedule.hour));
      newDate.setMinutes(Number(editNotification.schedule.minute));
      setSelectedTime(newDate);

      setTimezone(editNotification.schedule.timezone);

      if (Array.isArray(editNotification.members)) {
        if (editNotification.members.length === 0) {
          setMembers([allRecipientsOption]);
        } else {
          setMembers(
            editNotification.members.map((m: any) => {
              if (typeof m === "number") {
                return { value: m.toString(), label: `User ${m}` };
              }
              return {
                value: m.id.toString(),
                label: `${m.name} <${m.email}>`,
              };
            }),
          );
        }
      }
    }
  }, [editNotification, teamListOptions]);

  const handleClassesChange = (
    selected: MultiValue<{ value: string; label: string }> | null,
    _actionMeta: ActionMeta<{ value: string; label: string }>,
  ) => {
    setClasses((prev) => {
      if (!selected || selected.length === 0) return [];

      const hasAllNew = selected.some((opt) => opt.value === "ALL");
      const hadOnlyAllBefore = prev.length === 1 && prev[0].value === "ALL";

      if (hadOnlyAllBefore && hasAllNew && selected.length > 1) {
        return selected.filter((opt) => opt.value !== "ALL");
      }

      if (hasAllNew && selected.length > 1) {
        return [allClassesOption];
      }

      if (hasAllNew && selected.length === 1) {
        return [allClassesOption];
      }

      return [...selected];
    });
  };

  const handleRecipientsChange = (
    selected: MultiValue<{ value: string; label: string }> | null,
    _actionMeta: ActionMeta<{ value: string; label: string }>,
  ) => {
    setMembers((prev) => {
      if (!selected || selected.length === 0) return [];

      const hasAllNew = selected.some((opt) => opt.value === "ALL");
      const hadOnlyAllBefore = prev.length === 1 && prev[0].value === "ALL";

      if (hadOnlyAllBefore && hasAllNew && selected.length > 1) {
        return selected.filter((opt) => opt.value !== "ALL");
      }

      if (hasAllNew && selected.length > 1) {
        return [allRecipientsOption];
      }

      if (hasAllNew && selected.length === 1) {
        return [allRecipientsOption];
      }

      return [...selected];
    });
  };

  useEffect(() => {
    if (selectedTime) {
      setTimeInput(moment(selectedTime).format("HH:mm"));
    }
  }, [selectedTime]);

  return (
    <div className="modal-overlay">
      <div className="modal-panel p-5 w-[700px] relative max-h-[90vh] overflow-y-auto">
        <button
          aria-label="Close"
          className="absolute right-2 top-3 z-10 modal-close-btn"
          onClick={() => setShowAddNotificationModal(false)}
          type="button"
        >
          <FaTimes className="text-xl text-gray-500 hover:text-del-red" />
        </button>

        <h2 className="text-xl font-semibold text-center mb-2">
          {editNotification
            ? t("notification.editNotification")
            : t("notification.createNotification")}
        </h2>

        {!selectedTeam && !editNotification && (
          <div className="flex justify-center mb-2">
            <span className="px-3 py-1 rounded-full bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200 text-xs font-semibold">
              {step === 1 && t("notification.step1")}
              {step === 2 && t("notification.step2")}
            </span>
          </div>
        )}

        {step === 1 && (
          <form onSubmit={handleNextFromTeam} className="space-y-4 text-sm">
            <div>
              <label className="block mb-1 font-medium text-base">
                {t("notification.team")} <span className="text-del-red">*</span>
              </label>
              <Select
                defaultValue={selectedTeamOption ?? teamListOptions[0]}
                options={teamListOptions}
                value={team}
                onChange={(opt) => setTeam(opt ?? null)}
                classNamePrefix="react-select"
                required
                menuPortalTarget={document.body}
                menuPlacement="auto"
                menuPosition="fixed"
                styles={{
                  ...getSelectStyles(),
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                  option: (base, { isFocused }) => ({
                    ...base,
                    backgroundColor: isFocused
                      ? darkMode
                        ? "#86BC25"
                        : "#86BC25"
                      : darkMode
                        ? "#374151"
                        : "#FFFFFF",
                    color: darkMode ? "#FFFFFF" : "#000000",
                  }),
                }}
              />
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={() => setShowAddNotificationModal(false)}
                className="px-4 py-2 bg-gray-300 dark:bg-gray-700 rounded hover:bg-gray-400"
              >
                {t("feedback.cancel")}
              </button>
              <button
                type="submit"
                className={`px-4 py-2 text-sm ${
                  team ? "btn-green" : "btn-disabled"
                }`}
                disabled={!team}
              >
                {t("pagination.next")}
              </button>
            </div>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={handleSubmit} className="space-y-4 text-sm">
            <div>
              <label className="block mb-1 font-medium text-base">
                {t("notification.team")} <span className="text-del-red">*</span>
              </label>
              <Select
                isDisabled={true}
                defaultValue={selectedTeamOption ?? teamListOptions[0]}
                options={teamListOptions}
                value={team}
                onChange={(opt) => setTeam(opt ?? null)}
                classNamePrefix="react-select"
                styles={getSelectStyles(true)}
              />
            </div>

            <div>
              <label className="block mb-1 font-medium text-base">
                {t("notification.subject")}{" "}
                <span className="text-del-red">*</span>
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                required
                placeholder={t("notification.subjectPlaceholder")}
                className={`w-full px-3 py-1.5 text-sm border rounded focus:outline-none focus:ring-2 ${
                  darkMode
                    ? "bg-gray-700 text-gray-300 border-gray-600 focus:ring-green-500"
                    : "bg-gray-200 text-gray-700 border-gray-300 focus:ring-green-400"
                }`}
              />
            </div>

            <div>
              <label className="block mb-1 font-medium text-base">
                {t("notification.regulators")}{" "}
                <span className="text-del-red">*</span>
              </label>
              <Select
                isSearchable={true}
                isMulti
                options={teamData?.regulators?.map((r: any) => ({
                  value: r.id,
                  label: r.name,
                }))}
                value={regulators}
                onChange={(opt) => setRegulators(opt as any[])}
                classNamePrefix="react-select"
                styles={getSelectStyles()}
                placeholder={t("notification.searchAndSelectRegulator")}
                required
              />
            </div>

            {regulators.length > 0 && (
              <div>
                <label className="block mb-2 font-medium text-base">
                  {t("notification.classes")}
                </label>
                <Select
                  isSearchable={true}
                  isMulti
                  options={[
                    ...(defaultFilterData?.classes?.length
                      ? [{ value: "ALL", label: "All classes" }]
                      : []),
                    ...(defaultFilterData?.classes?.map((c: any) => ({
                      value: c,
                      label: c,
                    })) ?? []),
                  ]}
                  value={classesInput}
                  onChange={handleClassesChange}
                  classNamePrefix="react-select"
                  styles={getSelectStyles()}
                  placeholder={t("filtersSection.searchAndSelectClass")}
                />
              </div>
            )}

            <div>
              <label className="block mb-1 font-medium text-base">
                {t("notification.frequency")}
                <span className="text-del-red">*</span>
              </label>

              <div className="flex flex-wrap gap-2 mb-2">
                {weekdays.map((day) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => handleDayToggle(day)}
                    className={`px-3 py-1 rounded border ${
                      selectedDays.includes(day)
                        ? "bg-del-light-theme-green text-white"
                        : "bg-gray-200 dark:bg-gray-700"
                    }`}
                  >
                    {day}
                  </button>
                ))}
              </div>

              {daysError && (
                <p className="text-del-red text-sm">
                  {t("notification.selectAtLeastOneDay")}
                </p>
              )}

              <div className="flex items-center gap-x-4 w-full mt-2">
                <div className="flex gap-x-2 items-center">
                  <label className="font-medium text-base">
                    {t("notification.time")}
                  </label>
                  <CreatableSelect
                    options={timeOptions}
                    value={
                      selectedTime
                        ? {
                            value: moment(selectedTime).format("HH:mm"),
                            label: moment(selectedTime).format("HH:mm"),
                          }
                        : timeInput
                          ? { value: timeInput, label: timeInput }
                          : null
                    }
                    onChange={(opt: any) => {
                      if (opt && typeof opt.value === "string") {
                        const [hStr, mStr] = opt.value.split(":");
                        const h = Number(hStr);
                        const m = Number(mStr);
                        if (!Number.isNaN(h) && !Number.isNaN(m)) {
                          const newDate = new Date();
                          newDate.setHours(h, m, 0, 0);
                          setSelectedTime(newDate);
                          setTimeInput(opt.value);
                        }
                      } else {
                        setSelectedTime(null);
                        setTimeInput("");
                      }
                    }}
                    onInputChange={(val, actionMeta) => {
                      if (actionMeta.action === "input-change") {
                        let clean = val.replace(/\D/g, "");

                        if (clean.length > 2) {
                          clean = clean.slice(0, 2) + ":" + clean.slice(2);
                        }
                        if (clean.length > 5) clean = clean.slice(0, 5);

                        setTimeInput(clean);

                        if (/^\d{2}:\d{2}$/.test(clean)) {
                          const [h, m] = clean.split(":").map(Number);
                          if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
                            const newDate = new Date();
                            newDate.setHours(h, m, 0, 0);
                            setSelectedTime(newDate);
                          }
                        }
                        return clean;
                      }
                    }}
                    onCreateOption={(input) => {
                      if (/^\d{2}:\d{2}$/.test(input)) {
                        const [h, m] = input.split(":").map(Number);
                        if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
                          const newDate = new Date();
                          newDate.setHours(h, m, 0, 0);
                          setSelectedTime(newDate);
                          setTimeInput(input);
                          setTimeOptions((prev) => [
                            ...prev,
                            { value: input, label: input },
                          ]);
                        }
                      }
                    }}
                    isValidNewOption={(inputValue) => {
                      const clean = inputValue.replace(/\D/g, "");
                      if (clean.length !== 4) return false;
                      const hh = Number(clean.slice(0, 2));
                      const mm = Number(clean.slice(2, 4));
                      return (
                        !Number.isNaN(hh) &&
                        !Number.isNaN(mm) &&
                        hh >= 0 &&
                        hh <= 23 &&
                        mm >= 0 &&
                        mm <= 59
                      );
                    }}
                    formatCreateLabel={(inputValue) => {
                      const clean = inputValue.replace(/\D/g, "");
                      if (clean.length === 4) {
                        const hh = Number(clean.slice(0, 2));
                        const mm = Number(clean.slice(2, 4));
                        if (
                          !Number.isNaN(hh) &&
                          !Number.isNaN(mm) &&
                          hh >= 0 &&
                          hh <= 23 &&
                          mm >= 0 &&
                          mm <= 59
                        ) {
                          const formatted = `${clean.slice(0, 2)}:${clean.slice(2, 4)}`;
                          return `Create "${formatted}"`;
                        }
                      }
                      return " ";
                    }}
                    classNamePrefix="react-select"
                    placeholder="HH:mm"
                    styles={{
                      ...getSelectStyles(),
                      control: (base: any) => ({
                        ...base,
                        minWidth: "100px",
                        textAlign: "center",
                        backgroundColor: darkMode ? "#4B5563" : "#E5E7EB",
                      }),
                      input: (base: any) => ({
                        ...base,
                        color: darkMode ? "#fff" : "#000",
                        textAlign: "center",
                        caretColor: darkMode ? "#fff" : "#000",
                      }),
                      option: (base, { isFocused }) => ({
                        ...base,
                        backgroundColor: isFocused
                          ? darkMode
                            ? "#86BC25"
                            : "#86BC25"
                          : darkMode
                            ? "#374151"
                            : "#fff",
                        color: darkMode ? "#fff" : "#000",
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                    }}
                    menuPortalTarget={document.body}
                    menuPlacement="auto"
                    menuPosition="fixed"
                  />
                </div>

                <div className="flex gap-x-2 items-center">
                  <label className="font-medium text-base">
                    {t("notification.timezone")}
                  </label>
                  <Select
                    isSearchable={true}
                    options={timezoneOptions}
                    value={selectedTimezoneOption}
                    onChange={(opt) => setTimezone(opt?.value ?? "")}
                    classNamePrefix="react-select"
                    styles={{
                      ...getSelectStyles(),
                      input: (base: any) => ({
                        ...base,
                        color: darkMode ? "#FFFFFF" : "#000000",
                        minWidth: "175px",
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                      option: (base, { isFocused }) => ({
                        ...base,
                        backgroundColor: isFocused
                          ? darkMode
                            ? "#86BC25"
                            : "#86BC25"
                          : darkMode
                            ? "#374151"
                            : "#FFFFFF",
                        color: darkMode ? "#FFFFFF" : "#000000",
                        fontSize: "14px",
                        wordBreak: "break-all",
                      }),
                    }}
                    placeholder={t("Select timezone")}
                    menuPortalTarget={document.body}
                    menuPlacement="auto"
                    menuPosition="fixed"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block mb-1 font-medium text-base">
                {t("notification.recipients")}{" "}
                <span className="text-del-red">*</span>
              </label>
              <Select
                isSearchable={true}
                isMulti
                options={[
                  ...(teamData?.roles?.length
                    ? [{ value: "ALL", label: "All recipients" }]
                    : []),
                  ...(teamData?.roles?.map((role: any) => ({
                    value: role.user.id.toString(),
                    label: `${role.user.name} <${role.user.email}>`,
                  })) ?? []),
                ]}
                value={members}
                onChange={handleRecipientsChange}
                classNamePrefix="react-select"
                placeholder={t("notification.searchAndSelectRecipients")}
                menuPortalTarget={document.body}
                menuPlacement="auto"
                menuPosition="fixed"
                styles={{
                  ...getSelectStyles(),
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                  option: (base, { isFocused }) => ({
                    ...base,
                    backgroundColor: isFocused
                      ? darkMode
                        ? "#86BC25"
                        : "#86BC25"
                      : darkMode
                        ? "#374151"
                        : "#FFFFFF",
                    color: darkMode ? "#FFFFFF" : "#000000",
                  }),
                }}
              />
            </div>

            <div className="flex mt-6">
              {!editNotification && !selectedTeam && (
                <button
                  type="button"
                  onClick={handleBackFromDetails}
                  className="px-4 py-2 bg-gray-300 dark:bg-gray-700 rounded hover:bg-gray-400 text-sm"
                >
                  {t("newRecordModal.back")}
                </button>
              )}

              <div className="ml-auto flex gap-3">
                {editNotification && (
                  <button
                    type="button"
                    onClick={() => setShowAddNotificationModal(false)}
                    className="px-4 py-2 bg-gray-300 dark:bg-gray-700 rounded hover:bg-gray-400 text-sm"
                  >
                    {t("adminPanel.cancel")}
                  </button>
                )}
                <button
                  type="submit"
                  disabled={!isStep2Valid}
                  className={`px-4 py-2 text-sm ${isStep2Valid ? "btn-green" : "btn-disabled"}`}
                >
                  {editNotification ? t("adminPanel.save") : t("notification.create")}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export { AddEditNotificationModal };